import { useState, useMemo, useCallback, useRef, useEffect, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ClipboardList, FileText, ChevronDown, ChevronRight, Clock, Plus, CalendarDays } from 'lucide-react';
import { where } from 'firebase/firestore';
import MainLayout from '../components/layout/MainLayout';
import { Card, Input, Button, Modal } from '../components/ui';
import { useCollection } from '../hooks/useFirestore';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { agregarNotaPostConsulta } from '../services/consultaService';
import { fixMojibake } from '../utils/fixMojibake';
import { DISPOSICION_LABELS, type Consulta, type DisposicionConsulta } from '../types';
import { Badge } from '../components/ui/Badge';
import { Avatar } from '../components/ui/Avatar';
import { usePatientPhotos } from '../hooks/usePatientPhotos';
import styles from './HistorialPage.module.css';

/* Avatar pequeño de la columna Paciente (foto o iniciales). Estilo inline
   porque el tamaño es propio de esta tabla. */
const historialAvatarStyle: CSSProperties = {
  width: 24,
  height: 24,
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, var(--brand-green-700), var(--brand-green-900))',
  color: '#FFFEF5',
  fontSize: 9,
  fontWeight: 600,
  flexShrink: 0,
};

/* ---- Types ---- */

interface HistorialItem {
  id: string;
  pacienteId?: string;
  horaInicio: Date;
  finalizadaEn: Date | null;
  paciente: string;
  /** Firebase UID del paciente (para resolver la foto actual por users/{uid}). */
  pacienteUid?: string;
  /** Foto de perfil del paciente (denormalizada en la consulta); null → iniciales. */
  pacienteFotoURL?: string | null;
  motivo: string;
  duracionMin: number;
  sala: string;
  notas?: string;
  medicoId?: string;
  /** Nombre del médico que atendió — la supervisión necesita saber de quién
   *  es cada consulta, y el médico ya sabe que todas son suyas. */
  medico: string;
  /** Cierre de la consulta (disposición); undefined en registros antiguos. */
  disposicion?: DisposicionConsulta;
  /** True si la consulta fue atendida por el médico autenticado Y está dentro de la ventana de 24h */
  puedeAgregarNota: boolean;
  /** Horas restantes para agregar notas (truncado a 0 si pasó) */
  horasRestantes: number;
}

const VENTANA_NOTAS_HORAS = 24;

/* ---- Helpers ---- */

function formatTime(date: Date): string {
  return date.toLocaleTimeString('es-CR', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).toUpperCase();
}

/** Etiqueta de fecha: "Hoy" / "Ayer" / "Jueves 10" (+ año si difiere del actual). */
function formatDateLabel(date: Date): string {
  const now = new Date();
  const dayStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((dayStart(now) - dayStart(date)) / 86_400_000);
  if (diffDays === 0) return 'Hoy';
  if (diffDays === 1) return 'Ayer';
  const weekday = date.toLocaleDateString('es-CR', { weekday: 'long' });
  const cap = weekday.charAt(0).toUpperCase() + weekday.slice(1);
  let label = `${cap} ${date.getDate()}`;
  if (date.getFullYear() !== now.getFullYear()) label += `, ${date.getFullYear()}`;
  return label;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/* ---- Filtro de fechas ---- */

type DatePreset = 'hoy' | 'semana' | 'mes' | 'trimestre' | 'rango';

const DATE_PRESETS: Array<{ k: DatePreset; label: string }> = [
  { k: 'hoy', label: 'Hoy' },
  { k: 'semana', label: 'Esta semana' },
  { k: 'mes', label: 'Este mes' },
  { k: 'trimestre', label: 'Últimos 3 meses' },
  { k: 'rango', label: 'Rango de fechas' },
];

const startOfDay = (d: Date): Date => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay = (d: Date): Date => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };

/** Devuelve el rango [start, end] (o null = sin límite) según el preset. */
function computeRange(preset: DatePreset, customStart: string, customEnd: string): { start: Date | null; end: Date | null } {
  const now = new Date();
  switch (preset) {
    case 'hoy':
      return { start: startOfDay(now), end: endOfDay(now) };
    case 'semana': {
      // Semana actual: desde el lunes hasta hoy.
      const day = now.getDay(); // 0 = domingo
      const diffToMonday = day === 0 ? 6 : day - 1;
      const monday = startOfDay(now);
      monday.setDate(monday.getDate() - diffToMonday);
      return { start: monday, end: endOfDay(now) };
    }
    case 'mes':
      return { start: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), end: endOfDay(now) };
    case 'trimestre': {
      const start = startOfDay(now);
      start.setMonth(start.getMonth() - 3);
      return { start, end: endOfDay(now) };
    }
    case 'rango':
      return {
        start: customStart ? startOfDay(new Date(`${customStart}T00:00:00`)) : null,
        end: customEnd ? endOfDay(new Date(`${customEnd}T00:00:00`)) : null,
      };
  }
}

/* ---- Component ---- */

export default function HistorialPage() {
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const { addToast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  const [notaModalConsulta, setNotaModalConsulta] = useState<HistorialItem | null>(null);
  const [notaTexto, setNotaTexto] = useState('');
  const [notaSubmitting, setNotaSubmitting] = useState(false);
  // Filtro de fechas: por defecto solo las consultas de hoy.
  const [datePreset, setDatePreset] = useState<DatePreset>('hoy');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Quien ve la lista completa (supervisión y administración) necesita saber
  // quién atendió cada consulta. Al médico no se le muestra: todas son suyas.
  const muestraMedico = role !== 'medico';

  // Filtrar por médico (si role=medico ve solo las suyas). El administrador
  // (y otros roles no-médico) ven TODAS las consultas.
  const finalizadaConstraints = useMemo(() => {
    const base = [where('estado', '==', 'finalizada')];
    if (role === 'medico' && user?.uid) {
      return [...base, where('medicoId', '==', user.uid)];
    }
    return base;
  }, [role, user?.uid]);

  const dateRange = useMemo(
    () => computeRange(datePreset, customStart, customEnd),
    [datePreset, customStart, customEnd],
  );

  const { data: firestoreConsultas, error: firestoreError } = useCollection<Consulta>('consultas', finalizadaConstraints);

  // Convert Firestore data to HistorialItem format
  const historial: HistorialItem[] = useMemo(() => {
    if (firestoreError || !firestoreConsultas || firestoreConsultas.length === 0) {
      return [];
    }

    return firestoreConsultas.map((c) => {
      const inicio = c.iniciadaEn && typeof (c.iniciadaEn as any).toDate === 'function'
        ? (c.iniciadaEn as any).toDate()
        : (c.creadoEn && typeof (c.creadoEn as any).toDate === 'function'
          ? (c.creadoEn as any).toDate()
          : new Date());

      const fin: Date | null = c.finalizadaEn && typeof (c.finalizadaEn as any).toDate === 'function'
        ? (c.finalizadaEn as any).toDate()
        : null;

      const duracionMin = fin
        ? Math.max(1, Math.round((fin.getTime() - inicio.getTime()) / 60000))
        : 0;

      // Ventana de 24h para agregar notas. Sólo el médico que atendió.
      const horasTranscurridas = fin
        ? (Date.now() - fin.getTime()) / (60 * 60 * 1000)
        : Number.POSITIVE_INFINITY;
      const horasRestantes = Math.max(0, VENTANA_NOTAS_HORAS - horasTranscurridas);
      const puedeAgregarNota =
        role === 'medico' &&
        c.medicoId === user?.uid &&
        horasRestantes > 0;

      return {
        id: c.id,
        pacienteId: c.pacienteId,
        pacienteUid: c.pacienteUid,
        horaInicio: inicio,
        finalizadaEn: fin,
        paciente: fixMojibake(c.pacienteNombre || ''),
        pacienteFotoURL: c.pacienteFotoURL ?? null,
        motivo: c.motivoConsulta,
        duracionMin,
        sala: c.salaNombre,
        notas: c.notas,
        medicoId: c.medicoId,
        medico: fixMojibake(c.medicoNombre || ''),
        disposicion: c.disposicion,
        puedeAgregarNota,
        horasRestantes: Math.floor(horasRestantes),
      };
    }).sort((a, b) => b.horaInicio.getTime() - a.horaInicio.getTime());  // más recientes primero
  }, [firestoreConsultas, firestoreError, role, user?.uid]);

  const handleSubmitNota = useCallback(async () => {
    if (!notaModalConsulta || !notaTexto.trim() || notaSubmitting) return;
    setNotaSubmitting(true);
    try {
      const res = await agregarNotaPostConsulta(notaModalConsulta.id, notaTexto.trim());
      addToast('success', `Nota agregada. Quedan ${res.horasRestantes}h para más notas.`);
      setNotaModalConsulta(null);
      setNotaTexto('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al agregar nota';
      addToast('error', msg);
    } finally {
      setNotaSubmitting(false);
    }
  }, [notaModalConsulta, notaTexto, notaSubmitting, addToast]);

  /* Debounce search input by 300ms */
  useEffect(() => {
    timerRef.current = setTimeout(() => {
      setDebouncedTerm(searchTerm);
    }, 300);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [searchTerm]);

  /* Filtro por fecha (sobre la fecha de la consulta = horaInicio) */
  const dateFiltered = useMemo(() => {
    const { start, end } = dateRange;
    if (!start && !end) return historial;
    return historial.filter((item) => {
      const t = item.horaInicio.getTime();
      if (start && t < start.getTime()) return false;
      if (end && t > end.getTime()) return false;
      return true;
    });
  }, [historial, dateRange]);

  /* Filtro de búsqueda (sobre el conjunto ya filtrado por fecha) */
  const filtered = useMemo(() => {
    if (!debouncedTerm.trim()) return dateFiltered;
    const term = debouncedTerm.toLowerCase();
    return dateFiltered.filter(
      (item) =>
        item.paciente.toLowerCase().includes(term) ||
        item.motivo.toLowerCase().includes(term) ||
        item.medico.toLowerCase().includes(term),
    );
  }, [debouncedTerm, dateFiltered]);

  /* Fotos de perfil ACTUALES (resueltas por pacienteUid) para las filas
     visibles: cubre consultas viejas sin pacienteFotoURL denormalizado. */
  const fotosPorUid = usePatientPhotos(useMemo(
    () => filtered.map((i) => i.pacienteUid),
    [filtered],
  ));

  /* KPIs (sobre el rango de fechas seleccionado) */
  const kpis = useMemo(() => {
    const total = dateFiltered.length;
    const totalMin = dateFiltered.reduce((acc, i) => acc + i.duracionMin, 0);
    const avg = total > 0 ? Math.round(totalMin / total) : 0;
    return { total, avgDuration: formatDuration(avg), totalTime: formatDuration(totalMin) };
  }, [dateFiltered]);

  const toggleNotes = (id: string) => {
    setExpandedNotes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <MainLayout pageTitle="Historial">
      {/* KPI Cards */}
      <div className={styles.kpiGrid}>
        <Card className={styles.kpiCard}>
          <span className={styles.kpiValue}>{kpis.total}</span>
          <span className={styles.kpiLabel}>Consultas</span>
        </Card>
        <Card className={styles.kpiCard}>
          <span className={styles.kpiValue}>{kpis.avgDuration}</span>
          <span className={styles.kpiLabel}>Promedio</span>
        </Card>
        <Card className={styles.kpiCard}>
          <span className={styles.kpiValue}>{kpis.totalTime}</span>
          <span className={styles.kpiLabel}>Tiempo total</span>
        </Card>
      </div>

      {/* Filtro de fechas + alcance */}
      <div className={styles.filterBar}>
        <div className={styles.presetGroup} role="group" aria-label="Filtrar por fecha">
          {DATE_PRESETS.map((opt) => (
            <button
              key={opt.k}
              type="button"
              className={`${styles.presetBtn} ${datePreset === opt.k ? styles.presetBtnActive : ''}`}
              onClick={() => setDatePreset(opt.k)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {datePreset === 'rango' && (
          <div className={styles.rangeInputs}>
            <label className={styles.rangeField}>
              <span>Desde</span>
              <input
                type="date"
                className={styles.dateInput}
                value={customStart}
                max={customEnd || undefined}
                onChange={(e) => setCustomStart(e.target.value)}
              />
            </label>
            <label className={styles.rangeField}>
              <span>Hasta</span>
              <input
                type="date"
                className={styles.dateInput}
                value={customEnd}
                min={customStart || undefined}
                onChange={(e) => setCustomEnd(e.target.value)}
              />
            </label>
          </div>
        )}

        <span className={styles.scopeBadge} title={role === 'medico' ? 'Solo sus consultas' : 'Todas las consultas (administrador)'}>
          <CalendarDays size={13} aria-hidden="true" />
          {role === 'medico' ? 'Mis consultas' : 'Todas las consultas'}
        </span>
      </div>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.searchWrapper}>
          <Input
            leftIcon={<Search size={18} />}
            placeholder={muestraMedico ? 'Buscar por paciente, motivo o médico...' : 'Buscar por paciente o motivo...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Hint de acciones */}
      {filtered.length > 0 && (
        <p className={styles.hint}>
          <FileText size={13} aria-hidden="true" /> Clic en el <strong>paciente</strong> o en el <strong>motivo</strong> para abrir la consulta médica.
        </p>
      )}

      {/* Table or Empty State */}
      {filtered.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <ClipboardList size={36} />
          </div>
          <p className={styles.emptyMessage}>
            No hay consultas {role === 'medico' ? 'suyas ' : ''}en el periodo seleccionado.
          </p>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Hora inicio</th>
                <th>Paciente</th>
                {muestraMedico && <th>Médico</th>}
                <th>Motivo</th>
                <th>Duración</th>
                <th>Cierre</th>
                <th>Sala</th>
                <th>Notas</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => {
                const hasNotes = !!item.notas?.trim();
                const isExpanded = expandedNotes.has(item.id);

                return (
                  <tr key={item.id}>
                    <td>
                      <div className={styles.fechaCell}>
                        <span className={styles.fechaLabel}>{formatDateLabel(item.horaInicio)}</span>
                        <span className={styles.horaLabel}>{formatTime(item.horaInicio)}</span>
                      </div>
                    </td>
                    {/* El nombre abre la MISMA consulta que el motivo. Antes
                        llevaba al historial completo del paciente, y desde una
                        fila de una consulta concreta lo que se busca es esa
                        consulta; el historial del paciente se llega desde
                        dentro. */}
                    <td>
                      <button
                        type="button"
                        className={styles.patientLink}
                        onClick={() => navigate(`/historial/${item.id}`)}
                        title="Abrir la consulta médica"
                      >
                        <Avatar
                          nombre={item.paciente}
                          fotoURL={item.pacienteFotoURL ?? (item.pacienteUid ? fotosPorUid[item.pacienteUid] : null)}
                          style={historialAvatarStyle}
                          zoomable
                        />
                        <span>{item.paciente}</span>
                      </button>
                    </td>
                    {muestraMedico && (
                      <td className={styles.medicoCell}>
                        {item.medico || <span className={styles.medicoVacio}>Sin registrar</span>}
                      </td>
                    )}
                    <td className={styles.motivoCell}>
                      <button
                        type="button"
                        className={styles.motivoLink}
                        onClick={() => navigate(`/historial/${item.id}`)}
                        title="Abrir la consulta médica"
                      >
                        <span className={styles.motivoText}>{item.motivo}</span>
                        <FileText size={13} aria-hidden="true" className={styles.motivoIcon} />
                      </button>
                    </td>
                    <td>{formatDuration(item.duracionMin)}</td>
                    <td>
                      {item.disposicion ? (
                        <Badge
                          variant={
                            item.disposicion === 'completada' ? 'success'
                              : item.disposicion === 'referida' ? 'info'
                              : item.disposicion === 'incompleta' ? 'warning'
                              : 'error'
                          }
                          size="sm"
                        >
                          {DISPOSICION_LABELS[item.disposicion]}
                        </Badge>
                      ) : (
                        <Badge variant="neutral" size="sm">Finalizada</Badge>
                      )}
                    </td>
                    <td>{item.sala}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {hasNotes ? (
                        <div>
                          <button
                            type="button"
                            className={styles.notesToggle}
                            onClick={() => toggleNotes(item.id)}
                            aria-expanded={isExpanded}
                          >
                            <FileText size={14} />
                            {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                            {isExpanded ? 'Ocultar' : 'Ver notas'}
                          </button>
                          {isExpanded && (
                            <div className={styles.notesContent}>
                              {item.notas}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className={styles.noNotes}>—</span>
                      )}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {item.puedeAgregarNota ? (
                        <button
                          type="button"
                          onClick={() => {
                            setNotaModalConsulta(item);
                            setNotaTexto('');
                          }}
                          title={`Agregar nota (${item.horasRestantes}h restantes)`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '4px 10px',
                            border: '1px solid var(--color-primary)',
                            borderRadius: 'var(--radius-sm)',
                            background: 'var(--color-primary-soft)',
                            color: 'var(--color-primary-ink)',
                            cursor: 'pointer',
                            fontSize: 'var(--font-size-xs)',
                            fontWeight: 'var(--font-weight-semibold)',
                          }}
                        >
                          <Plus size={12} aria-hidden="true" /> Nota
                          <Clock size={10} aria-hidden="true" style={{ opacity: 0.6 }} />
                          <span style={{ opacity: 0.7, fontSize: 10 }}>{item.horasRestantes}h</span>
                        </button>
                      ) : (
                        <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal: agregar nota post-consulta */}
      <Modal
        open={!!notaModalConsulta}
        onClose={() => { if (!notaSubmitting) setNotaModalConsulta(null); }}
        title="Agregar nota a la consulta"
      >
        {notaModalConsulta && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <div style={{
              padding: 'var(--space-3)',
              background: 'var(--color-surface-tint)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-secondary)',
            }}>
              <strong>Paciente:</strong> {notaModalConsulta.paciente}<br />
              <strong>Motivo:</strong> {notaModalConsulta.motivo}<br />
              <strong>Tiempo restante:</strong> {notaModalConsulta.horasRestantes}h
              {' '}<span style={{ fontStyle: 'italic' }}>
                (no podrá editar o eliminar esta nota una vez guardada)
              </span>
            </div>
            <label style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Nueva nota
            </label>
            <textarea
              value={notaTexto}
              onChange={(e) => setNotaTexto(e.target.value.slice(0, 5000))}
              placeholder="Anote información adicional sobre la consulta..."
              rows={6}
              disabled={notaSubmitting}
              style={{
                width: '100%',
                padding: 'var(--space-3)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-surface)',
                color: 'var(--color-text-primary)',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--font-size-sm)',
                lineHeight: 'var(--line-height-normal)',
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
            <p style={{ fontSize: 10, color: 'var(--color-text-tertiary)', textAlign: 'right', margin: 0, fontFamily: 'var(--font-mono)' }}>
              {notaTexto.length} / 5000
            </p>
            <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end', paddingTop: 'var(--space-2)', borderTop: '1px solid var(--color-border-soft)' }}>
              <Button variant="ghost" onClick={() => setNotaModalConsulta(null)} disabled={notaSubmitting}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmitNota}
                loading={notaSubmitting}
                disabled={!notaTexto.trim() || notaSubmitting}
              >
                Guardar nota
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </MainLayout>
  );
}
